/* ══════════════════════════════════════════════════════════════
   Prediction Markets — Polymarket Integration
   Fetches trending prediction/betting data from Polymarket
   ══════════════════════════════════════════════════════════════ */

interface PredictionMarket {
  id: string;
  question: string;
  yesPrice: number;
  noPrice: number;
  volume: number;
  category: string;
  endDate: string;
}

type PolymarketApiResponse = Array<{
  condition_id?: string;
  question?: string;
  tokens?: Array<{ price?: number }>;
  volume_num_min?: number;
  end_date_iso?: string;
  tags?: string[];
}>;

const PREDICTION_CATEGORIES = ['All', 'Politics', 'Crypto', 'Sports', 'World', 'Economy'];
let activeCategory = 'All';
let predictionRefreshTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Fetch markets from Polymarket's public CLOB API
 */
async function fetchPredictionMarkets(): Promise<PredictionMarket[]> {
  try {
    const response = await fetch('https://clob.polymarket.com/markets?limit=20&active=true&closed=false');

    if (!response.ok) throw new Error('Polymarket API error');

    const data: PolymarketApiResponse = await response.json() as PolymarketApiResponse;

    if (Array.isArray(data) && data.length > 0) {
      return data
        .filter(m => m.question)
        .map(m => ({
          id: m.condition_id || crypto.randomUUID(),
          question: m.question || 'Unknown',
          yesPrice: m.tokens?.[0]?.price ?? 0.5,
          noPrice: m.tokens?.[1]?.price ?? 0.5,
          volume: m.volume_num_min ?? 0,
          category: classifyMarket(m.question || '', m.tags || []),
          endDate: m.end_date_iso ?? '',
        }));
    }

    return [];
  } catch {
    console.warn('[PredictionMarkets] API failed');
    return [];
  }
}

function classifyMarket(question: string, tags: string[]): string {
  const text = (question + ' ' + tags.join(' ')).toLowerCase();
  if (/trump|biden|election|president|congress|poll|vote|senate|democrat|republican/.test(text)) return 'Politics';
  if (/bitcoin|ethereum|crypto|btc|eth|defi|web3/.test(text)) return 'Crypto';
  if (/nfl|nba|soccer|football|world cup|olympics|champion|game|match|league/.test(text)) return 'Sports';
  if (/gdp|fed|rate|inflation|recession|market|stock|economy|trade/.test(text)) return 'Economy';
  return 'World';
}


function formatVolume(vol: number): string {
  if (vol >= 1000000) return `$${(vol / 1000000).toFixed(1)}M`;
  if (vol >= 1000) return `$${(vol / 1000).toFixed(0)}K`;
  return `$${vol}`;
}

/**
 * Render Prediction Markets panel
 */
export function renderPredictionMarkets(container: HTMLElement): void {
  container.innerHTML = `
    <div class="wm-loading">
      <div class="wm-loading__spinner"></div>
      Loading prediction markets...
    </div>
  `;

  loadPredictions(container);

  predictionRefreshTimer = setInterval(() => loadPredictions(container), 120000);
}

async function loadPredictions(container: HTMLElement): Promise<void> {
  const markets = await fetchPredictionMarkets();

  const filtered = activeCategory === 'All'
    ? markets
    : markets.filter(m => m.category === activeCategory);

  const tabs = PREDICTION_CATEGORIES.map(cat => `
    <button class="wm-prediction-tab ${cat === activeCategory ? 'wm-prediction-tab--active' : ''}" data-cat="${cat}">
      ${cat}
    </button>
  `).join('');

  const items = filtered.map(m => {
    const yesPercent = Math.round(m.yesPrice * 100);
    const noPercent = 100 - yesPercent;
    return `
      <div class="wm-prediction-item">
        <div class="wm-prediction-item__title">${escapeHtml(m.question)}</div>
        <div class="wm-prediction-bar">
          <div class="wm-prediction-bar__yes" style="width:${yesPercent}%">YES ${yesPercent}%</div>
          <div class="wm-prediction-bar__no" style="width:${noPercent}%">NO ${noPercent}%</div>
        </div>
        <div class="wm-prediction-item__meta">
          <span>Vol: ${formatVolume(m.volume)}</span>
          <span>${m.category}</span>
        </div>
      </div>
    `;
  }).join('');

  container.innerHTML = `
    <div class="wm-prediction-tabs">${tabs}</div>
    <div class="wm-prediction-list">
      ${items || '<div class="wm-empty">No markets in this category</div>'}
    </div>
  `;

  // Tab click handlers
  container.querySelectorAll('.wm-prediction-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      activeCategory = (btn as HTMLElement).dataset.cat || 'All';
      loadPredictions(container);
    });
  });
}

function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

export function destroyPredictionMarkets(): void {
  if (predictionRefreshTimer) clearInterval(predictionRefreshTimer);
}
